/**
 * A aba Sessões (docs/spec/session-clock.md, "A aba Sessões"): a sessão de jogo aberta no alto (o
 * tempo grande, a linha do tempo, quem está e as notas) e as anteriores numa lista, cada uma abrindo
 * o registro dela. Todos na mesa veem; o mestre controla o relógio e escreve o título e as notas. Na
 * sessão encerrada, as avaliações (SessionRatings.tsx).
 *
 * O registro vem por REST (`/api/rooms/:id/sessions`) e é relido a cada mudança do relógio ou das
 * notas (`gameSessionsVersion`, que sobe com o `gameSession.state` e o `gameSession.history`).
 */
import { useEffect, useState } from 'react';
import { api } from '../../api.js';
import { useLanguage, useT } from '../../i18n/index.js';
import { GAME_SESSION_LIMITS, gameSessionTimes, isGameSessionOpen, nextGameSessionNumber, type GameSession, type GameSessionDetail, type GameSessionSummary } from '../../shared.js';
import { describeError, useApp } from '../../store.js';
import { Spinner } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import { askConfirm } from '../../ui/prompt.js';
import { StarAverage } from '../../ui/StarRating.js';
import { toast } from '../../ui/Toaster.js';
import { useIsRoomGm } from '../audio/MusicPlayerModule.js';
import { useRoomClock, useSessionRules } from './ClockPill.js';
import { formatClock, formatDuration, pausesText, playAnchor, LOCAL_FORMATS } from './clockModel.js';
import { badgeLabel, ratingBadges, type RatingBadge } from './ratingsModel.js';
import { SessionRatings } from './SessionRatings.js';
import { dateBlock, dayLabel, lastSessionText, mergePage, pastSessions, people, personSpans, personText, sessionFacts, timeRange, timeline } from './sessionsModel.js';
import { useServerNow } from './useServerNow.js';
import './sessions.css';

type SessionList = { roomId: string; sessions: GameSessionSummary[]; hasMore: boolean };

/**
 * As sessões da mesa. A cada mudança relê a primeira página e mantém as mais velhas que o "Ver mais"
 * já trouxe.
 */
function useSessionList(roomId: string | null, version: number) {
  const token = useApp((s) => s.token);
  const [list, setList] = useState<SessionList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    if (!token || !roomId) return;
    let alive = true;
    api.roomSessions(token, roomId).then(
      (page) => {
        if (!alive) return;
        setError(null);
        setList((prev) => {
          const lowest = page.sessions[page.sessions.length - 1]?.number ?? Infinity;
          const older = prev?.roomId === roomId ? prev.sessions.filter((session) => session.number < lowest) : [];
          return { roomId, sessions: mergePage(page.sessions, older), hasMore: older.length && prev ? prev.hasMore : page.hasMore };
        });
      },
      (failure: unknown) => alive && setError(describeError(failure)),
    );
    return () => {
      alive = false;
    };
  }, [token, roomId, version]);
  const loadMore = () => {
    const last = list?.sessions[list.sessions.length - 1];
    if (!token || !roomId || !list || !last || loadingMore) return;
    setLoadingMore(true);
    api.roomSessions(token, roomId, last.number).then(
      (page) => setList((prev) => (prev?.roomId === roomId ? { roomId, sessions: mergePage(prev.sessions, page.sessions), hasMore: page.hasMore } : prev)),
      (failure: unknown) => toast(describeError(failure), 'error'),
    ).finally(() => setLoadingMore(false));
  };
  return { list: list?.roomId === roomId ? list : null, error, loadMore, loadingMore };
}

/** O registro de uma sessão (as notas, os passos e a presença), relido a cada mudança. */
function useSessionDetail(roomId: string | null, number: number | null, version: number) {
  const token = useApp((s) => s.token);
  const [loaded, setLoaded] = useState<{ key: string; detail: GameSessionDetail } | null>(null);
  const key = roomId && number ? `${roomId}:${number}` : null;
  useEffect(() => {
    if (!token || !roomId || !number) return;
    let alive = true;
    api.roomSession(token, roomId, number).then(
      (detail) => alive && setLoaded({ key: `${roomId}:${number}`, detail }),
      (failure: unknown) => alive && toast(describeError(failure), 'error'),
    );
    return () => {
      alive = false;
    };
  }, [token, roomId, number, version]);
  return loaded && loaded.key === key ? loaded.detail : null;
}

export function SessionsModule() {
  const t = useT();
  const roomId = useApp((s) => s.activeRoomId);
  const version = useApp((s) => (roomId ? (s.gameSessionsVersion[roomId] ?? 0) : 0));
  const enabled = useApp((s) => s.serverFeatures.gameSessions);
  // sem o registro (docs/spec/session-clock.md, "Ligar e desligar"), o aviso, enquanto a aba fecha
  const record = useSessionRules().record;
  const clock = useRoomClock();
  const admin = useIsRoomGm();
  const { list, error, loadMore, loadingMore } = useSessionList(enabled && record ? roomId : null, version);
  const [expanded, setExpanded] = useState<number | null>(null);
  const now = useServerNow(true, 60_000);

  // levada até uma sessão (o ★ do relógio, o Ver de um aviso): o cartão dela abre, e as avaliações
  // rolam para a vista quando o registro chegar
  const focus = useApp((s) => s.sessionFocus);
  const settleFocus = useApp((s) => s.settleSessionFocus);
  const [focused, setFocused] = useState<{ number: number; nonce: number } | null>(null);
  if (focus && focus.roomId === roomId && focus.nonce !== focused?.nonce) {
    setFocused({ number: focus.number, nonce: focus.nonce });
    setExpanded(focus.number);
  }
  useEffect(() => {
    if (focused) settleFocus(focused.nonce, 'shown');
  }, [focused, settleFocus]);

  if (!enabled) return <div className="rd-module__placeholder">{t('sessions.unavailable')}</div>;
  if (!record) return <div className="rd-module__placeholder">{t('sessions.off')}</div>;
  const live = clock?.session ?? null;
  const past = list ? pastSessions(list.sessions) : [];
  return (
    <div className="rd-sessions">
      <div className="rd-sessions__body">
        {isGameSessionOpen(live) ? (
          <CurrentSession session={live} roomId={roomId} version={version} admin={admin} />
        ) : (
          <IdleSession next={nextGameSessionNumber(live)} last={past[0] ?? null} empty={!!list && !list.sessions.length} admin={admin} roomId={roomId} now={now} />
        )}

        <section className="rd-sessions__past" aria-label={t('sessions.past')}>
          <h3 className="rd-sessions__heading">
            {t('sessions.past')}
            {past.length ? <span className="rd-sessions__count">{`${past.length}${list?.hasMore ? '+' : ''}`}</span> : null}
          </h3>
          {error && !list ? (
            <p className="rd-sessions__error" role="alert">
              {error}
            </p>
          ) : !list ? (
            <div className="rd-module__placeholder">
              <Spinner size={22} />
            </div>
          ) : !past.length ? (
            <p className="rd-sessions__muted">{t('sessions.past.empty')}</p>
          ) : (
            <ul className="rd-sessions__rows">
              {past.map((session) => (
                <PastSession
                  key={session.number}
                  session={session}
                  open={expanded === session.number}
                  onToggle={() => setExpanded((current) => (current === session.number ? null : session.number))}
                  roomId={roomId}
                  version={version}
                  admin={admin}
                  now={now}
                  focus={focused?.number === session.number ? focused.nonce : 0}
                  onDeleted={() => setExpanded(null)}
                />
              ))}
            </ul>
          )}
          {list?.hasMore ? (
            <button type="button" className="rd-btn rd-btn--secondary rd-sessions__more" disabled={loadingMore} onClick={loadMore}>
              {t('sessions.more')}
            </button>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function StartButton({ number }: { number: number }) {
  const t = useT();
  const online = useApp((s) => s.status === 'online');
  const control = useApp((s) => s.controlGameSession);
  return (
    <button type="button" className="rd-btn rd-btn--action" disabled={!online} onClick={() => control('start')}>
      <Icon name="play" size={12} strokeWidth={0} filled />
      {t('clock.start', { n: number })}
    </button>
  );
}

/**
 * Sem sessão aberta: a próxima (com o "Começar" do mestre) e a última, para situar. Enquanto a última
 * está aberta para avaliar, o botão leva às avaliações dela: "Avaliar a sessão N", para quem jogou e
 * ainda não deu a nota, ou "Avaliar os jogadores", para o mestre que ainda não avaliou algum.
 */
function IdleSession({ next, last, empty, admin, roomId, now }: { next: number; last: GameSessionSummary | null; empty: boolean; admin: boolean; roomId: string | null; now: number }) {
  const t = useT();
  const focusSession = useApp((s) => s.focusGameSession);
  const rate = !!last && !!roomId && ratingBadges(last.ratings, now).some((badge) => badge.kind === 'rate');
  const rateLabel = last?.ratings?.mine === null ? t('sessions.rating.rateSession', { n: last.number }) : t('sessions.rating.ratePlayers', { n: last?.number ?? 0 });
  return (
    <section className="rd-sessions__hero rd-sessions__idle" aria-label={t('sessions.next')}>
      {admin ? (
        <div className="rd-sessions__heroText">
          <div className="rd-sessions__eyebrow">{t('sessions.next')}</div>
          <h2 className="rd-sessions__title">{t('sessions.untitled', { n: next })}</h2>
        </div>
      ) : (
        <p className="rd-sessions__lead">{t('sessions.none')}</p>
      )}
      {empty ? <p className="rd-sessions__meta">{t('sessions.empty')}</p> : last ? <p className="rd-sessions__meta">{lastSessionText(t, last)}</p> : null}
      {admin || rate ? (
        <div className="rd-sessions__controls">
          {admin ? <StartButton number={next} /> : null}
          {rate && last && roomId ? (
            <button type="button" className="rd-btn rd-btn--secondary" onClick={() => focusSession(roomId, last.number)}>
              <Icon name="star" size={13} />
              {rateLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** A sessão aberta: o tempo correndo, os botões do mestre e o registro até agora. */
function CurrentSession({ session, roomId, version, admin }: { session: GameSession; roomId: string | null; version: number; admin: boolean }) {
  const t = useT();
  const online = useApp((s) => s.status === 'online');
  const control = useApp((s) => s.controlGameSession);
  const now = useServerNow(session.status === 'running', 1000, playAnchor(session));
  const detail = useSessionDetail(roomId, session.number, version);
  const paused = session.status === 'paused';
  const { playedMs } = gameSessionTimes(session, now);
  // o estado do relógio é o mais novo; o título, as notas e os nomes vêm do registro
  const summary: GameSessionSummary = { title: '', endedAt: null, startedByName: '', endedByName: null, ...detail?.session, ...session };
  const end = async () => {
    const ok = await askConfirm({
      title: t('clock.end'),
      message: t('clock.end.confirm', { n: session.number, played: formatDuration(t, playedMs) }),
      action: t('clock.end.yes'),
      danger: true,
    });
    if (ok) control('end');
  };
  return (
    <section className={`rd-sessions__hero rd-sessions__current ${paused ? 'is-paused' : 'is-running'}`} aria-label={t('sessions.untitled', { n: session.number })}>
      <div className="rd-sessions__heroTop">
        <div className="rd-sessions__heroText">
          <div className="rd-sessions__eyebrow">
            {t('sessions.eyebrow', { n: session.number, day: dayLabel(t, session.startedAt, true) })}
            <span className={`rd-sessions__status${paused ? ' is-paused' : ''}`}>
              <span className="rd-sessions__statusDot" aria-hidden="true" />
              {t(paused ? 'sessions.status.paused' : 'sessions.status.running')}
            </span>
          </div>
          {summary.title ? <h2 className="rd-sessions__title">{summary.title}</h2> : null}
          <div className="rd-sessions__big" role="timer" aria-label={t(paused ? 'clock.timer.paused' : 'clock.timer', { n: session.number, played: formatDuration(t, playedMs) })}>
            {formatClock(playedMs)}
          </div>
          <p className="rd-sessions__meta">
            {t('sessions.startedAt', { time: LOCAL_FORMATS.time(session.startedAt) })}
            {pausesText(t, session, now)}
          </p>
        </div>
        {admin ? (
          <div className="rd-sessions__controls">
            <button type="button" className={`rd-btn ${paused ? 'rd-btn--action' : 'rd-btn--secondary'}`} disabled={!online} onClick={() => control(paused ? 'resume' : 'pause')}>
              <Icon name={paused ? 'play' : 'pause'} size={12} strokeWidth={0} filled />
              {t(paused ? 'clock.resume' : 'clock.pause')}
            </button>
            <button type="button" className="rd-btn rd-btn--secondary" disabled={!online} onClick={() => void end()}>
              <Icon name="stop" size={11} strokeWidth={0} filled />
              {t('clock.end.menu')}
            </button>
          </div>
        ) : null}
      </div>
      <SessionRecord summary={summary} detail={detail} roomId={roomId} admin={admin} now={now} current />
    </section>
  );
}

/**
 * Uma sessão que já terminou, como um cartão do diário: a data, o título e os fatos, o tempo de jogo
 * à direita; aberta, o registro dela.
 */
function PastSession({
  session,
  open,
  onToggle,
  roomId,
  version,
  admin,
  now,
  focus,
  onDeleted,
}: {
  session: GameSessionSummary;
  open: boolean;
  onToggle: () => void;
  roomId: string | null;
  version: number;
  admin: boolean;
  now: number;
  focus: number;
  onDeleted: () => void;
}) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const date = dateBlock(t, session.startedAt);
  const title = session.title || t('sessions.untitled', { n: session.number });
  const facts = sessionFacts(t, session, now);
  const played = formatDuration(t, session.playedMs);
  const badges = ratingBadges(session.ratings, now);
  return (
    <li className={`rd-sessions__row${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="rd-sessions__rowHead"
        aria-expanded={open}
        aria-label={[title, dayLabel(t, session.startedAt, true), ...facts, t('sessions.row.played', { played }), ...badges.map((badge) => badgeLabel(t, badge, language))].join('. ')}
        onClick={onToggle}
      >
        <span className="rd-sessions__date" aria-hidden="true">
          <span className="rd-sessions__dateSmall">{date.weekday}</span>
          <span className="rd-sessions__dateDay">{date.day}</span>
          <span className="rd-sessions__dateSmall">{date.month}</span>
        </span>
        <span className="rd-sessions__rowText">
          <span className="rd-sessions__rowTitleLine">
            <span className="rd-sessions__rowTitle">{title}</span>
            {badges.map((badge) => (
              <RatingChip key={badge.kind} badge={badge} />
            ))}
          </span>
          <span className="rd-sessions__rowFacts">
            {facts.map((fact, index) => (
              <span key={index}>{fact}</span>
            ))}
          </span>
        </span>
        <span className="rd-sessions__rowPlayed">
          <strong>{played}</strong>
          <span>{t('sessions.row.playedLabel')}</span>
        </span>
        <span className="rd-sessions__chevron" aria-hidden="true">
          <Icon name="chevronDown" size={14} />
        </span>
      </button>
      {open ? <PastRecord session={session} roomId={roomId} version={version} admin={admin} now={now} focus={focus} onDeleted={onDeleted} /> : null}
    </li>
  );
}

/** Um selo das avaliações no cartão: a média (mestre), a nota que dei, ou o Avaliar em destaque. */
function RatingChip({ badge }: { badge: RatingBadge }) {
  const t = useT();
  if (badge.kind === 'average') {
    return (
      <span className="rd-sessions__chip" aria-hidden="true">
        <StarAverage average={badge.average} size={12} />
        <span className="rd-sessions__chipCount">{badge.count}</span>
      </span>
    );
  }
  if (badge.kind === 'mine') {
    return (
      <span className="rd-sessions__chip is-mine" aria-hidden="true" title={t('sessions.rating.badge.mine', { n: badge.stars })}>
        <Icon name="star" size={11} strokeWidth={1.6} filled />
        {badge.stars}
      </span>
    );
  }
  return (
    <span className="rd-sessions__chip is-rate" aria-hidden="true">
      <Icon name="star" size={11} />
      {t('sessions.rating.rate')}
    </span>
  );
}

function PastRecord({
  session,
  roomId,
  version,
  admin,
  now,
  focus,
  onDeleted,
}: {
  session: GameSessionSummary;
  roomId: string | null;
  version: number;
  admin: boolean;
  now: number;
  focus: number;
  onDeleted: () => void;
}) {
  const detail = useSessionDetail(roomId, session.number, version);
  if (!detail) {
    return (
      <div className="rd-sessions__record rd-module__placeholder">
        <Spinner size={18} />
      </div>
    );
  }
  return <SessionRecord summary={{ ...session, ...detail.session }} detail={detail} roomId={roomId} admin={admin} now={now} current={false} focus={focus} onDeleted={onDeleted} />;
}

/**
 * O registro de uma sessão: a linha do tempo, quem esteve, as notas (com o "Editar" do mestre), na
 * encerrada as avaliações e, para o mestre, o pé com o apagar.
 */
function SessionRecord({
  summary,
  detail,
  roomId,
  admin,
  now,
  current,
  focus = 0,
  onDeleted,
}: {
  summary: GameSessionSummary;
  detail: GameSessionDetail | null;
  roomId: string | null;
  admin: boolean;
  now: number;
  current: boolean;
  focus?: number;
  onDeleted?: () => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  if (!detail) {
    return (
      <div className="rd-sessions__record rd-module__placeholder">
        <Spinner size={18} />
      </div>
    );
  }
  const line = timeline(summary, detail.events, now);
  const who = people(detail.presence, summary, now);
  const notes = detail.session.notes;
  const pauses = line.segments.some((segment) => segment.kind === 'pause');
  return (
    <div className="rd-sessions__record">
      <div className="rd-sessions__section">
        <div className="rd-sessions__timeline" role="img" aria-label={t('sessions.timeline', { range: timeRange(t, summary, now) })}>
          <div className="rd-sessions__bar">
            {line.segments.map((segment, index) => (
              <span key={index} className={`rd-sessions__seg is-${segment.kind}`} style={{ left: `${segment.from * 100}%`, width: `${(segment.to - segment.from) * 100}%` }} />
            ))}
          </div>
          {line.now !== null ? <span className="rd-sessions__now" style={{ left: `${line.now * 100}%` }} /> : null}
          <div className="rd-sessions__ticks" aria-hidden="true">
            {line.ticks.map((tick, index) => (
              <span key={index} className={`is-${tick.align}${tick.near ? ' is-near' : ''}`} style={{ left: `${tick.pos * 100}%` }}>
                {tick.label}
              </span>
            ))}
          </div>
        </div>
        {/* a legenda só serve quando há pausa: sem ela, a barra é toda de jogo */}
        {pauses ? (
          <div className="rd-sessions__key" aria-hidden="true">
            <span>
              <i className="is-play" />
              {t('sessions.key.play')}
            </span>
            <span>
              <i className="is-pause" />
              {t('sessions.key.pause')}
            </span>
          </div>
        ) : null}
      </div>

      <div className="rd-sessions__section">
        <h4 className="rd-sessions__subheading">{t(current ? 'sessions.people' : 'sessions.people.past')}</h4>
        {who.length ? (
          <ul className="rd-sessions__people">
            {who.map((person) => (
              <li key={person.key} className={person.present ? 'is-present' : undefined} title={personSpans(person)}>
                <span className="rd-sessions__avatar" aria-hidden="true">
                  {person.name.slice(0, 1).toUpperCase()}
                </span>
                <span className="rd-sessions__name">{person.name}</span>
                {person.role === 'gm' ? <span className="rd-sessions__role">{t('sessions.role.gm')}</span> : null}
                <span className="rd-sessions__when">{personText(t, person)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rd-sessions__muted">—</p>
        )}
      </div>

      <div className="rd-sessions__section">
        <div className="rd-sessions__sectionHead">
          <h4 className="rd-sessions__subheading">{t('sessions.notes')}</h4>
          {admin && !editing ? (
            <button type="button" className="rd-sessions__editBtn" onClick={() => setEditing(true)}>
              <Icon name="edit" size={13} />
              {t('sessions.edit')}
            </button>
          ) : null}
        </div>
        {editing && admin ? (
          <NotesEditor summary={summary} notes={notes} roomId={roomId} onDone={() => setEditing(false)} />
        ) : notes ? (
          <p className="rd-sessions__notes">{notes}</p>
        ) : (
          <p className="rd-sessions__muted">{t('sessions.notes.empty')}</p>
        )}
      </div>

      {current ? null : <SessionRatings number={summary.number} ratings={detail.ratings} roomId={roomId} now={now} focus={focus} />}

      {admin ? <DeleteSession number={summary.number} roomId={roomId} current={current} onDeleted={onDeleted} /> : null}
    </div>
  );
}

/**
 * O pé do registro, para o mestre: apagar a sessão (docs/spec/session-clock.md, "Apagar uma sessão").
 * Pergunta antes; o registro e as linhas dela no chat somem para todos, e a aberta para o relógio.
 */
function DeleteSession({ number, roomId, current, onDeleted }: { number: number; roomId: string | null; current: boolean; onDeleted?: (() => void) | undefined }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!token || !roomId || busy) return;
    const ok = await askConfirm({
      title: t('sessions.delete.title', { n: number }),
      message: t(current ? 'sessions.delete.confirmOpen' : 'sessions.delete.confirm'),
      action: t('sessions.delete.yes'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    api.deleteRoomSession(token, roomId, number).then(
      () => {
        toast(t('sessions.deleted', { n: number }), 'success');
        onDeleted?.();
      },
      (failure: unknown) => {
        setBusy(false);
        toast(describeError(failure), 'error');
      },
    );
  };
  return (
    <div className="rd-sessions__recordFoot">
      <button type="button" className="rd-sessions__deleteBtn" disabled={busy} onClick={() => void remove()}>
        <Icon name="trash" size={13} />
        {t('sessions.delete')}
      </button>
    </div>
  );
}

/** O mestre escreve o título e as notas; salvar avisa a mesa, e a aba de todos relê. */
function NotesEditor({ summary, notes, roomId, onDone }: { summary: GameSessionSummary; notes: string; roomId: string | null; onDone: () => void }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const [title, setTitle] = useState(summary.title);
  const [text, setText] = useState(notes);
  const [saving, setSaving] = useState(false);
  const save = () => {
    if (!token || !roomId || saving) return;
    setSaving(true);
    api.updateRoomSession(token, roomId, summary.number, { title, notes: text }).then(
      () => onDone(),
      (failure: unknown) => {
        setSaving(false);
        toast(describeError(failure), 'error');
      },
    );
  };
  return (
    <form
      className="rd-sessions__editor"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <label className="rd-sessions__field">
        <span>{t('sessions.edit.title')}</span>
        <span className="rd-edit rd-edit--filled">
          <input className="rd-edit__input" value={title} maxLength={GAME_SESSION_LIMITS.title} placeholder={t('sessions.untitled', { n: summary.number })} onChange={(e) => setTitle(e.target.value)} />
        </span>
      </label>
      <label className="rd-sessions__field">
        <span>{t('sessions.notes')}</span>
        <textarea
          className="rd-edit rd-edit--filled rd-sessions__notesInput"
          rows={6}
          value={text}
          maxLength={GAME_SESSION_LIMITS.notes}
          placeholder={t('sessions.edit.notesPlaceholder')}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="rd-sessions__editorActions">
        <button type="submit" className="rd-btn rd-btn--action" disabled={saving}>
          {t('sessions.save')}
        </button>
        <button type="button" className="rd-btn rd-btn--secondary" disabled={saving} onClick={onDone}>
          {t('sessions.cancel')}
        </button>
      </div>
    </form>
  );
}
